/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { ACLS } from '@config/constants/acls';
import { NOT_APPLICABLE } from '@config/constants/common';
import { AREC_ORGANIZATION_IDS } from '@config/constants/organizations';
import { ROLES } from '@config/constants/roles';
import { TApiParams, TParams } from '@global-types/index';
import { apiClient } from '@lib/api-client';
import { useAuthorization } from '@lib/authorization';
import { useQuery } from '@tanstack/react-query';
import { getCountryName } from '@utils/countries';
import { isNumber } from '@utils/functions';
import { getTechnologyProject } from '@utils/project-technologies';
import { TApiResponse } from '../types';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const getAdditionalityProjects = (
	params: TApiParams,
	isArec: boolean
): Promise<TApiResponse> => {
	const { queryParams, page, pageSize } = params;
	const { name, ...restQueryParams } = queryParams;
	return apiClient.get('/additionalities', {
		params: {
			page: page,
			pageSize: pageSize,
			query: restQueryParams,
			sort: `-active -financing.unlimited -financing.remaining_euro name${isArec ? ' supportedBy' : ''}`,
			...(name && {
				search: name,
				searchFields: ['name', 'location.place'],
			}),
			populate: `cover, logo, publicSpecs, ${isArec ? 'supportedBy.logo,' : ''} organization`,
		},
	});
};

export const getPublicAdditionalityProjects = (
	params: TApiParams,
	isArec: boolean
): Promise<TApiResponse> => {
	const { queryParams, page, pageSize } = params;
	const { name, ...restQueryParams } = queryParams;
	return apiClient.get('/public/additionalities', {
		params: {
			page: page,
			pageSize: pageSize,
			query: restQueryParams,
			sort: `${isArec ? '-supportedBy ' : ''}-active -financing.unlimited -financing.remaining_euro name`,
			active: true,
			...(name && {
				search: name,
				searchFields: ['name', 'location.place'],
			}),
			populate: `cover, logo, publicSpecs, ${isArec ? 'supportedBy.logo,' : ''} organization`,
		},
	});
};

/* -------------------------------------------------------------------------- */
/*                                    Hooks                                   */
/* -------------------------------------------------------------------------- */
export const useAdditionalityProjects = (
	page: number,
	size: number,
	queryParams: TParams
) => {
	const {
		checkAcl,
		checkRole,
		user: cachedUser,
	} = useAuthorization();

	const isArec: boolean =
		!!cachedUser?.organization &&
		Object.values(AREC_ORGANIZATION_IDS).includes(
			cachedUser.organization
		);

	const { data, isFetching, isFetched, isPending } =
		useQuery({
			queryKey: [
				'additionality-projects',
				{
					page: page,
					size: size,
					queryParams: queryParams,
				},
			],
			queryFn: () => {
				if (
					!checkRole([
						ROLES.ADMIN,
						ROLES.SUPERADMIN,
						ROLES.STAFF,
						ROLES.PRODUCER_ADMIN,
						ROLES.PRODUCER_STAFF,
						ROLES.ORGANIZATION_ADMIN,
						ROLES.ORGANIZATION_STAFF,
					]) &&
					!checkAcl(ACLS.ADDITIONALITIES.LIST)
				) {
					return getPublicAdditionalityProjects(
						{
							page: page,
							pageSize: size,
							queryParams: queryParams,
						},
						isArec
					);
				}
				return getAdditionalityProjects(
					{
						page: page,
						pageSize: size,
						queryParams: queryParams,
					},
					isArec
				);
			},
			staleTime: 0,
		});

	const updateList = (projects: any) =>
		projects.reduce((acc: any, currentItem: any) => {
			const technolgyProject = getTechnologyProject(
				currentItem.technology
			);
			const countryName = getCountryName(
				currentItem?.location?.countryCode ?? ''
			);

			const unlimited =
				currentItem.financing &&
				currentItem.financing.unlimited
					? currentItem.financing.unlimited
					: 0;
			const financialNeeds = unlimited
				? 'Unlimited'
				: currentItem.financing &&
					  isNumber(currentItem.financing.target_euro)
					? currentItem.financing &&
						isNumber(currentItem.financing.target_euro)
						? currentItem.financing.target_euro
						: NOT_APPLICABLE
					: 0;
			const coverage =
				currentItem.financing &&
				isNumber(currentItem.financing.pledged_euro)
					? currentItem.financing.pledged_euro.toString()
					: 0;

			const percent =
				currentItem.financing &&
				isNumber(currentItem.financing.target_euro) &&
				isNumber(currentItem.financing.remaining_euro)
					? ((currentItem.financing.target_euro -
							currentItem.financing.remaining_euro) /
							currentItem.financing.target_euro) *
						100
					: 0;

			const progress = unlimited
				? 40
				: Math.round(percent);

			const cover =
				currentItem && currentItem.cover
					? currentItem.cover
					: technolgyProject
						? technolgyProject.image
						: '';

			const modifiedItem = {
				...currentItem,
				id: currentItem.id,
				name: currentItem.name,
				country: {
					name: countryName,
					code: currentItem.location.countryCode,
				},
				region: currentItem.location.place,
				type: technolgyProject
					? technolgyProject.label
					: '',
				image: cover,
				cover,
				price: 0,
				supportedBy:
					currentItem?.supportedBy?.logo || null,
				financialNeeds: financialNeeds,
				coverage: coverage,
				remaining_euro: currentItem?.financing
					?.remaining_euro
					? currentItem?.financing.remaining_euro
					: 0,
				progress: progress,
				isDisabled:
					Number(financialNeeds) - Number(coverage) == 0,
				active: currentItem?.active,
				financing: currentItem?.financing,
				isPool: currentItem?.isPool,
				organization:
					currentItem?.organization?.name ||
					NOT_APPLICABLE,
			};
			acc.push(modifiedItem);

			return acc;
		}, []);

	return {
		data: data ? updateList(data?.rows) : [],
		isLoading: isFetching && !isFetched,
		isFetched,
		isFetching,
		total: data?.total,
		page: data?.page,
		pageSize: data?.pageSize,
		totalPages: data?.totalPages,
		isPending,
	};
};
